import type { FeedbackPayload } from '@/components/PatientPageV2/components/PrescriptionPad';
import {
  PrescriptionPadStep,
  transformAndMergeMedicationsAndItemsToReissue,
  transformFeedbackPayload,
  transformV3DeviceProductsToV4DeviceProducts,
} from '@/components/PatientPageV2/components/PrescriptionPad';
import { usePrescriptionAssistantTracking } from '@/components/PatientPageV2/hooks/usePrescriptionAssistantTracking';
import type { NetworkError } from '@/error-handling/types';
import { StatusCode } from '@/error-handling/types';
import { usePrescribePrescriptions } from '@/hooks/prescription/usePrescribePrescriptions';
import { usePostProductSurvey } from '@/hooks/rest/usePostProductSurvey';
import { useAppStore } from '@/state-management';
import { useState } from 'react';
import { createPrimaryButtonLabel, handleResponse, showErrorNotification } from './PrescriptionAssistantTool.utils';

export const usePrescriptionAssistant = () => {
  const { mutateAsync: mutatePrescriptions } = usePrescribePrescriptions();

  const {
    prescriptionPad: {
      step: createScriptStep,
      devicePadItems: devicePadItems,
      medicationPadItems: medicationPadItems,
      dispenseNote,
    },
    prescriptionHistory: { itemsToReissue },
  } = useAppStore.use.prescriptionAssistant();

  const productSurveyMutation = usePostProductSurvey();
  const feedbackPayload = transformFeedbackPayload(medicationPadItems);

  const updatePrescriptionPadStep = useAppStore.use.updatePrescriptionPadStep();
  const resetPrescriptionPad = useAppStore.use.resetPrescriptionPad();
  const resetPrescriptionHistory = useAppStore.use.resetPrescriptionHistory();

  const { trackPrescriptionCreated } = usePrescriptionAssistantTracking();

  const [isLoading, setIsLoading] = useState(false);
  const [isPostingPrescription, setIsPostingPrescription] = useState(false);
  const [showPrescribeStateError, setShowPrescribeStateError] = useState(false);

  const primaryButtonLabel = createPrimaryButtonLabel(createScriptStep);

  const submitProductSurveyFeedback = async (
    feedbackPayload: FeedbackPayload[],
    doctorId: number,
    patientId: number,
  ) => {
    if (!feedbackPayload?.length) {
      return;
    }

    productSurveyMutation?.mutate({
      doctorId,
      patientId,
      feedback: feedbackPayload,
    });
  };

  const createPrescriptionsPayload = transformAndMergeMedicationsAndItemsToReissue(medicationPadItems, itemsToReissue);

  const createPrescriptionAPI = async (patientId: string) => {
    const validDevices = devicePadItems.filter((device) => !!device);
    const payload = {
      orderDate: new Date(),
      products: [...createPrescriptionsPayload],
      devices: transformV3DeviceProductsToV4DeviceProducts(validDevices),
      ...(!!dispenseNote.length && { dispenseNote: dispenseNote.trim() }),
    };

    return mutatePrescriptions({
      params: { patientId },
      // @ts-expect-error: TS2339: type needs to be fixed
      //orderDate is expected to be a string
      body: payload,
    });
  };

  const handleSubmitScript = async (patientId: string, doctorId: number) => {
    setIsLoading(true);
    setIsPostingPrescription(true);
    try {
      const response = await createPrescriptionAPI(patientId);
      // @ts-expect-error: TS2345: type needs to be fixed
      await handleResponse(response);
      submitProductSurveyFeedback(feedbackPayload, doctorId, Number(patientId));

      if (response.body?.prescription?.id !== undefined) {
        trackPrescriptionCreated({
          prescriptionId: response.body.prescription.id,
        });
      }

      return {
        success: true,
      };
    } catch (networkError: unknown) {
      if ((networkError as NetworkError).status === StatusCode.Conflict) {
        setShowPrescribeStateError(true);
      } else {
        showErrorNotification();
      }
      return {
        success: false,
      };
    } finally {
      setIsLoading(false);
      setIsPostingPrescription(false);
    }
  };

  const resetPrescriptionAssistant = () => {
    resetPrescriptionPad();
    resetPrescriptionHistory();
    updatePrescriptionPadStep(PrescriptionPadStep.Create);
  };

  return {
    primaryButtonLabel,
    isPostingPrescription,
    isLoading,
    showPrescribeStateError,
    resetPrescriptionAssistant,
    handleSubmitScript,
    setShowPrescribeStateError,
  };
};
